import React, { useState } from 'react';
import { Building2, Loader2, Trash2, UserPlus } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@shared/components/ui/dialog';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Avatar, AvatarFallback } from '@shared/components/ui/avatar';
import { Badge } from '@shared/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@shared/components/ui/select';
import { useOrgsQuery } from '@shared/hooks/useOrgsQuery';
import {
  useCollaboratorsQuery, useAddCollaboratorMutation,
  useUpdateCollaboratorMutation, useRemoveCollaboratorMutation,
} from '@shared/hooks/useSharedNotesQuery';
import { CollaboratorPermission, NoteCollaborator } from '@shared/types';

interface ShareNoteModalProps {
  noteId: string;
  open: boolean;
  onClose: () => void;
  /** Whether the signed-in user owns this note (owners manage collaborators). */
  isOwner: boolean;
}

const PERMISSIONS: { value: CollaboratorPermission; label: string }[] = [
  { value: 'VIEW', label: 'Can view' },
  { value: 'COMMENT', label: 'Can comment' },
  { value: 'EDIT', label: 'Can edit' },
];

function PermissionSelect({ value, onChange, disabled, compact }: {
  value: CollaboratorPermission;
  onChange: (p: CollaboratorPermission) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  return (
    <Select value={value} onValueChange={v => onChange(v as CollaboratorPermission)} disabled={disabled}>
      <SelectTrigger className={compact ? 'h-7 w-32 text-xs' : 'h-9 w-36 text-sm'}><SelectValue /></SelectTrigger>
      <SelectContent>
        {PERMISSIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

/** Share a note with people (by email) or a whole organization. */
export default function ShareNoteModal({ noteId, open, onClose, isOwner }: ShareNoteModalProps) {
  const { data: collaborators = [], isLoading } = useCollaboratorsQuery(open ? noteId : null);
  const { data: memberships = [] } = useOrgsQuery(open);
  const addCollaborator = useAddCollaboratorMutation(noteId);
  const updateCollaborator = useUpdateCollaboratorMutation(noteId);
  const removeCollaborator = useRemoveCollaboratorMutation(noteId);

  const [email, setEmail] = useState('');
  const [emailPerm, setEmailPerm] = useState<CollaboratorPermission>('EDIT');
  const [orgId, setOrgId] = useState('');
  const [orgPerm, setOrgPerm] = useState<CollaboratorPermission>('VIEW');

  const orgs = memberships.map(m => m.org).filter(Boolean);

  const handleAddPerson = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    await addCollaborator.mutateAsync({ email: trimmed, permission: emailPerm });
    setEmail('');
  };

  const handleShareOrg = async () => {
    if (!orgId) return;
    await addCollaborator.mutateAsync({ org_id: orgId, permission: orgPerm });
    setOrgId('');
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Share note</DialogTitle>
        </DialogHeader>

        {isOwner && (
          <div className="space-y-4">
            {/* Add person */}
            <div>
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Invite a person
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="h-9 flex-1 text-sm"
                  onKeyDown={e => { if (e.key === 'Enter') handleAddPerson(); }}
                />
                <PermissionSelect value={emailPerm} onChange={setEmailPerm} />
                <Button size="sm" className="h-9" onClick={handleAddPerson} disabled={!email.trim() || addCollaborator.isPending}>
                  {addCollaborator.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            {/* Share with org */}
            {orgs.length > 0 && (
              <div>
                <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Share with an organization
                </div>
                <div className="flex items-center gap-2">
                  <Select value={orgId} onValueChange={setOrgId}>
                    <SelectTrigger className="h-9 flex-1 text-sm">
                      <SelectValue placeholder="Choose organization…" />
                    </SelectTrigger>
                    <SelectContent>
                      {orgs.map(o => (
                        <SelectItem key={o!.org_id} value={o!.org_id}>{o!.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <PermissionSelect value={orgPerm} onChange={setOrgPerm} />
                  <Button size="sm" className="h-9" onClick={handleShareOrg} disabled={!orgId || addCollaborator.isPending}>
                    Share
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Collaborator list */}
        <div>
          <div className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Who has access
          </div>
          {isLoading ? (
            <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : collaborators.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">Only you have access to this note.</p>
          ) : (
            <div className="divide-y divide-border rounded-lg border border-border">
              {collaborators.map((c: NoteCollaborator) => {
                const isOrgShare = !!c.org_id;
                const name = isOrgShare ? c.org?.name ?? 'Organization' : (c.user?.name ?? c.user?.email ?? 'User');
                return (
                  <div key={c.collaborator_id} className="flex items-center gap-3 px-3 py-2.5">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs">
                        {isOrgShare ? <Building2 className="h-4 w-4" /> : name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{name}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {isOrgShare ? 'Everyone in the organization' : c.user?.email}
                      </div>
                    </div>
                    {isOwner ? (
                      <>
                        <PermissionSelect
                          compact
                          value={c.permission}
                          onChange={p => updateCollaborator.mutate({ collaboratorId: c.collaborator_id, permission: p })}
                        />
                        <button
                          onClick={() => removeCollaborator.mutate(c.collaborator_id)}
                          className="text-muted-foreground hover:text-destructive"
                          title="Remove access"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <Badge variant="secondary" className="text-[10px]">{c.permission}</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
